<template>
  <ClientOnly>
    <section v-if="lastOrder || items.length > 0" class="ordered-carousel">
      <h2 class="ordered-carousel__title">Вы заказывали</h2>

      <swiper-container
        class="ordered-carousel__slider"
        :space-between="16"
        :slides-per-view="2.2"
        :breakpoints="breakpoints"
      >
        <!-- Повторить последний заказ -->
        <swiper-slide v-if="lastOrder" class="ordered-carousel__slide">
          <div
            class="oc-card oc-card--repeat"
            :class="{ 'oc-card--disabled': isRepeating }"
            role="button"
            tabindex="0"
            @click="onRepeatLast"
            @keydown.enter="onRepeatLast"
            @keydown.space.prevent="onRepeatLast"
          >
            <div class="oc-card__img repeat-collage" :class="`repeat-collage--${collageImages.length}`">
              <img
                v-for="(url, i) in collageImages"
                :key="i"
                :src="url"
                alt=""
                loading="lazy"
                class="repeat-collage__img"
                @error="onImgError"
              />
              <span class="repeat-collage__icon">
                <UIcon name="i-heroicons-arrow-path" :class="{ 'animate-spin': isRepeating }" />
              </span>
            </div>
            <div class="oc-card__title">Повторить последний заказ</div>
          </div>
        </swiper-slide>

        <!-- Ранее заказанные товары (по популярности) -->
        <swiper-slide
          v-for="item in items"
          :key="item.id"
          class="ordered-carousel__slide"
        >
          <div class="oc-card" @click="openProduct(item)">
            <div class="oc-card__img">
              <video
                v-if="nodeMedia(item).isVideo"
                :src="nodeMedia(item).url"
                playsinline
                muted
                loop
                autoplay
                preload="metadata"
                class="oc-card__media"
                :aria-label="item.name"
              />
              <img
                v-else
                :src="nodeMedia(item).url"
                :alt="item.name"
                loading="lazy"
                class="oc-card__media"
                @error="onImgError"
              />
            </div>

            <div class="oc-card__title">{{ item.name }}</div>

            <div class="oc-card__bottom">
              <div class="oc-card__cost">{{ priceLabel(item) }}</div>
              <div class="oc-card__button">
                <UiButtonMain
                  label=""
                  :icon="isItemInCart(item) ? 'i-heroicons-shopping-cart-solid' : 'i-heroicons-plus-16-solid'"
                  color="red"
                  size="md"
                  block
                  :trailing="true"
                  @click.stop="openProduct(item)"
                />
              </div>
            </div>
          </div>
        </swiper-slide>
      </swiper-container>
    </section>
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useOrderedProducts, useRepeatOrder, type RepeatOrderMode, type RepeatSkippedItem } from '../model'
import { useCartStore } from '@entities/order'
import { useInstitutionStore } from '@entities/institution'
import { useUserProfileStore } from '@entities/user'
import { useModalStore, modalStateEnum, useAlertStore } from '@shared/model'
import { isVideoFile, setProductOpenSource } from '@shared/lib'

const { items, lastOrder, load, reset } = useOrderedProducts()
const { isRepeating, repeatOrder } = useRepeatOrder()

const cartStore = useCartStore()
const institutionStore = useInstitutionStore()
const userProfileStore = useUserProfileStore()
const modalStore = useModalStore()
const alertStore = useAlertStore()

const breakpoints = {
  320: { slidesPerView: 2.2, spaceBetween: 12 },
  400: { slidesPerView: 2.6, spaceBetween: 12 },
  640: { slidesPerView: 4.2, spaceBetween: 14 },
  1024: { slidesPerView: 4.7, spaceBetween: 16 },
  1280: { slidesPerView: 4.2, spaceBetween: 16 },
  1680: { slidesPerView: 5.2, spaceBetween: 16 },
  1920: { slidesPerView: 6.2, spaceBetween: 16 },
}

// Загружаем при готовности пользователя и филиала (без storeAuth — по getUserId)
watch(
  () => [userProfileStore.getUserId, institutionStore.selectedBranch?.id] as const,
  ([userId, branchId]) => {
    if (userId && branchId) {
      load()
    } else if (!userId) {
      reset()
    }
  },
  { immediate: true },
)

const placeholderImg = computed(() => (institutionStore.institution as any)?.visual?.placeholder_file ?? '')

/** Первый url медиа из массива (объекты {url} или строки) */
function firstUrl(images: any): string | null {
  if (!Array.isArray(images) || images.length === 0) return null
  const first = images[0]
  if (first && typeof first === 'object' && first.url) return first.url
  if (typeof first === 'string') return first
  return null
}

/** Картинка узла меню (single/combo — img; группа — img первого варианта) */
function nodeImageUrl(item: any): string {
  const direct = firstUrl(item?.img)
  if (direct) return direct
  if (item?.type === 'group' && Array.isArray(item.variants)) {
    for (const variant of item.variants) {
      const u = firstUrl(variant?.img)
      if (u) return u
    }
  }
  return placeholderImg.value
}

/** Медиа узла (с поддержкой видео) */
function nodeMedia(item: any): { url: string; isVideo: boolean } {
  const url = nodeImageUrl(item)
  return { url, isVideo: isVideoFile(url) }
}

/** Подпись цены: группа/комбо → «от N ₽», одиночный → «N ₽» */
function priceLabel(item: any): string {
  if (item?.type === 'combo') {
    return `от ${Number(item.final_price ?? item.base_price ?? item.price ?? 0)} ₽`
  }
  if (item?.type === 'group' && Array.isArray(item.variants) && item.variants.length > 0) {
    const min = Math.min(...item.variants.map((v: any) => Number(v.price) || 0))
    return `от ${min} ₽`
  }
  return `${Number(item?.price) || 0} ₽`
}

/** До 4 картинок позиций последнего заказа для коллажа */
const collageImages = computed<string[]>(() => {
  const orderItems = (lastOrder.value?.items ?? []) as any[]
  const urls: string[] = []
  for (const it of orderItems) {
    const url = firstUrl(it?.img) ?? firstUrl(it?.product?.img)
    if (url) urls.push(url)
    if (urls.length >= 4) break
  }
  if (urls.length === 0 && placeholderImg.value) urls.push(placeholderImg.value)
  return urls
})

function onImgError(event: Event): void {
  const img = event.target as HTMLImageElement
  if (placeholderImg.value && !img.src.includes('placeholder')) {
    img.src = placeholderImg.value
  }
}

/** Открыть карточку товара (как в каталоге) */
function openProduct(item: any): void {
  setProductOpenSource('ordered_before')
  cartStore.setSelectedDish(item)
  modalStore.open(modalStateEnum.MoreAboutDishRefactored)
}

/** Есть ли товар в корзине (для иконки кнопки) */
function isItemInCart(item: any): boolean {
  const branchId = institutionStore.selectedBranch?.id
  if (!branchId) return false
  if (item?.type === 'combo') {
    return cartStore.cart.some(
      (c: any) => c.itemType === 'combo' && String(c.comboId) === String(item.id) && String(c.branch_id) === String(branchId),
    )
  }
  if (item?.type === 'group' && Array.isArray(item.variants)) {
    return item.variants.some((v: any) =>
      cartStore.cart.some((c: any) => String(c.id) === String(v.id) && String(c.branch_id) === String(branchId)),
    )
  }
  return cartStore.cart.some((c: any) => String(c.id) === String(item.id) && String(c.branch_id) === String(branchId))
}

// ===== Повтор последнего заказа =====
const SKIP_REASON_TEXT: Record<RepeatSkippedItem['reason'], string> = {
  unavailable: 'нет в меню',
  stop_list: 'нет в наличии',
  combo_no_data: 'набор недоступен',
}

function skippedMessage(skipped: RepeatSkippedItem[]): string {
  return skipped.map(s => `${s.name} (${SKIP_REASON_TEXT[s.reason]})`).join(', ')
}

/** Спросить про непустую корзину: true — заменить, false — добавить */
function askReplace(): boolean {
  if (typeof window === 'undefined') return false
  return window.confirm(
    'В корзине уже есть товары.\n\nОК — очистить и заменить последним заказом.\nОтмена — добавить к текущим.',
  )
}

async function onRepeatLast(): Promise<void> {
  if (!lastOrder.value || isRepeating.value) return

  let mode: RepeatOrderMode = 'append'
  if (cartStore.cart.length > 0) {
    mode = askReplace() ? 'replace' : 'append'
  }

  try {
    const result = await repeatOrder(lastOrder.value, mode)

    if (result.addedCount === 0) {
      alertStore.error(
        result.skipped.length > 0
          ? `Не удалось добавить: ${skippedMessage(result.skipped)}`
          : 'В заказе нет позиций для повтора',
      )
      return
    }

    if (result.skipped.length > 0) {
      alertStore.warn(`Не добавлены: ${skippedMessage(result.skipped)}`, 7000)
    }
    alertStore.success('Товары добавлены в корзину')
    cartStore.openBasket()
  } catch (error: any) {
    alertStore.error(error?.message || 'Не удалось повторить заказ')
  }
}
</script>

<style lang="postcss">
.ordered-carousel {
  @apply w-full mb-2;

  &__title {
    @apply text-2xl lg:text-3xl font-semibold mb-6 md:mb-8 text-gray-900 dark:text-white;
  }

  &__slider {
    @apply w-full py-2;
  }

  &__slide {
    @apply h-auto;
  }
}

/* Карточка — копия визуала каталожной .dish-card */
.oc-card {
  @apply relative flex flex-col gap-4 p-3 lg:p-5 text-center cursor-pointer h-full;
  @apply rounded-3xl overflow-visible;
  user-select: none;
  background: linear-gradient(135deg, rgba(229, 231, 235, 1), rgba(229, 231, 235, 0.5));
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.05),
    0 10px 15px -3px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.08),
      0 20px 25px -5px rgba(0, 0, 0, 0.08);
    border-color: rgba(239, 68, 68, 0.3);
  }

  @media (max-width: 768px) {
    @apply rounded-2xl p-2.5 gap-3;
  }

  &__img {
    @apply relative rounded-2xl overflow-hidden w-full flex-shrink-0;
    aspect-ratio: 1 / 1;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.05));

    @media (max-width: 768px) {
      @apply rounded-xl;
    }
  }

  &__media {
    @apply w-full h-full object-cover object-center;
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover &__media {
    transform: scale(1.05);
  }

  &__title {
    @apply text-sm lg:text-base font-bold tracking-tight leading-snug;
    @apply text-gray-900 dark:text-white;
    flex-shrink: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-wrap: break-word;

    @media (max-width: 768px) {
      @apply text-xs;
      line-height: 1.3;
    }
  }

  &__bottom {
    @apply grid grid-cols-[1fr_36px] gap-2 items-center mt-auto;
  }

  /* Компактная красная кнопка */
  &__button button {
    @apply w-9 h-9 p-0 min-h-0 justify-center;
  }

  &__cost {
    @apply text-sm md:text-base lg:text-lg font-bold tracking-tight text-gray-900 dark:text-white;
    font-variant-numeric: tabular-nums;
  }

  /* --- вариант «Повторить последний заказ» --- */
  &--repeat {
    @apply justify-start;
  }

  &--disabled {
    @apply opacity-60 cursor-not-allowed pointer-events-none;
  }
}

/* Коллаж последнего заказа внутри .oc-card__img */
.repeat-collage {
  @apply grid gap-1;
  grid-template-columns: 1fr 1fr;

  &--1 {
    grid-template-columns: 1fr;
  }

  &__img {
    @apply w-full h-full object-cover;
  }

  &--1 &__img {
    @apply col-span-2;
  }

  &__icon {
    @apply absolute inset-0 m-auto flex items-center justify-center;
    @apply w-12 h-12 rounded-full bg-orange-500 text-white text-2xl shadow-lg;
  }
}

/* Тёмная тема — как у каталога */
.dark .oc-card {
  background: linear-gradient(135deg, rgba(24, 24, 27, 1), rgba(24, 24, 27, 0.5));
  border-color: rgba(255, 255, 255, 0.1);

  &:hover {
    border-color: rgba(239, 68, 68, 0.4);
  }

  .oc-card__img {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.01));
  }
}
</style>
